import { validateToolFeedback } from './toolFeedbackValidation';
import ToolAvailability from './ToolAvailability';
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Wrench, Plus, Pencil, Trash, Copy, AlertTriangle } from 'lucide-react';
import ModernTimeDropdown from '../../../dashboard-components/layouts/modernDropdown';
import MessageWithOverrides from './MessageWithOverrides';
import { getMessageRules, validateMessageRules } from './messageRules';
import { TextareaWithVariables } from './VariableInsertion.jsx';
import { BOOKING_VERIFICATION_INSTRUCTIONS } from './preToolVerification';

const HTTP_METHODS = ['POST', 'PUT', 'PATCH', 'GET', 'DELETE'];
const TOOL_TYPES = ['http_request', 'context_injection', 'llm_call', 'send_email'];
const CONVERSATION_ID_HEADER = 'X-Anthropod-Conversation-Id';
const AGENT_ID_HEADER = 'X-Anthropod-Agent-Id';
const RESERVED_RUNTIME_HEADERS = new Set([
  CONVERSATION_ID_HEADER.toLowerCase(),
  AGENT_ID_HEADER.toLowerCase()
]);
const RESERVED_TOOL_NAMES = new Set([
  'context_injector',
  'end_conversation',
  'http_tool',
  'knowledge_base_retrieve',
  'llm_call',
  'transfer_call'
]);
const DEFAULT_LLM_MODELS = {
  'gpt-4.1': { label: 'GPT-4.1' },
  'gpt-4.1-mini': { label: 'GPT-4.1 Mini' },
  'gemini-2.5-flash': { label: 'Gemini 2.5 Flash' },
  'gemini-2.5-flash-lite': { label: 'Gemini 2.5 Flash-Lite' }
};
const EMAIL_TOOL_PARAMETERS = {
  type: 'object',
  properties: {
    subject: {
      type: 'string',
      description: 'Subject of the email to send.'
    },
    message: {
      type: 'string',
      description: 'Complete plain-text email message.'
    }
  },
  required: ['subject', 'message']
};

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function isValidEmailAddress(value) {
  const email = String(value || '').trim();
  return email.length <= 320 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
}

function parseJsonSafely(text) {
  try {
    if (!isNonEmptyString(text)) return { ok: true, value: undefined };
    const parsed = JSON.parse(text);
    return { ok: true, value: parsed };
  } catch (e) {
    return { ok: false, error: 'Invalid JSON' };
  }
}

function extractPlaceholdersFromString(str) {
  if (typeof str !== 'string') return [];
  const regex = /\{\{\s*([\w.-]+)\s*\}\}/g;
  const found = new Set();
  let match;
  while ((match = regex.exec(str)) !== null) {
    if (match[1]) found.add(match[1]);
  }
  return Array.from(found);
}

function extractPlaceholdersFromObject(obj) {
  const found = new Set();
  const visit = (val) => {
    if (val == null) return;
    if (typeof val === 'string') {
      extractPlaceholdersFromString(val).forEach(p => found.add(p));
    } else if (Array.isArray(val)) {
      val.forEach(visit);
    } else if (typeof val === 'object') {
      Object.values(val).forEach(visit);
    }
  };
  visit(obj);
  return Array.from(found);
}

function toObjectFromRows(rows) {
  const out = {};
  rows.forEach(({ key, value }) => {
    if (isNonEmptyString(key)) out[key] = value ?? '';
  });
  return out;
}

function toRowsFromObject(obj) {
  if (!obj || typeof obj !== 'object') return [{ key: '', value: '' }];
  const rows = Object.entries(obj).map(([key, value]) => ({ key, value: String(value) }));
  return rows.length > 0 ? rows : [{ key: '', value: '' }];
}

function toDefaultsObject(rows) {
  const out = {};
  rows.forEach(({ key, value }) => {
    if (!isNonEmptyString(key)) return;
    if (value === 'true') out[key] = true;
    else if (value === 'false') out[key] = false;
    else if (value !== '' && !Number.isNaN(Number(value))) out[key] = Number(value);
    else out[key] = value;
  });
  return out;
}

function toDefaultsRows(obj) {
  if (!obj || typeof obj !== 'object') return [{ key: '', value: '' }];
  const rows = Object.entries(obj).map(([key, value]) => ({ key, value: String(value) }));
  return rows.length > 0 ? rows : [{ key: '', value: '' }];
}

function paramsRowsFromObject(parameters) {
  if (!parameters || typeof parameters !== 'object') return [{ name: '', type: 'string', required: false, description: '' }];
  const rows = Object.entries(parameters).map(([name, def]) => ({
    name,
    type: def?.type || 'string',
    required: !!def?.required,
    description: def?.description || ''
  }));
  return rows.length > 0 ? rows : [{ name: '', type: 'string', required: false, description: '' }];
}

function paramsObjectFromRows(rows) {
  const out = {};
  rows.forEach(({ name, type, required, description }) => {
    if (!isNonEmptyString(name)) return;
    out[name] = {
      type: isNonEmptyString(type) ? type : 'string',
      required: !!required,
      description: description || ''
    };
  });
  return out;
}

function defaultValueFromType(type) {
  switch (type) {
    case 'integer':
    case 'number':
      return 123;
    case 'boolean':
      return true;
    case 'string':
    default:
      return 'TEST_STRING';
  }
}

function generateExampleFromSchema(schema) {
  if (!schema || typeof schema !== 'object') return undefined;
  const type = schema.type;
  if (schema.enum && Array.isArray(schema.enum) && schema.enum.length > 0) {
    return schema.enum[0];
  }
  if (schema.anyOf && Array.isArray(schema.anyOf) && schema.anyOf.length > 0) {
    return generateExampleFromSchema(schema.anyOf[0]);
  }
  if (schema.oneOf && Array.isArray(schema.oneOf) && schema.oneOf.length > 0) {
    return generateExampleFromSchema(schema.oneOf[0]);
  }
  if (type === 'object') {
    const result = {};
    const props = schema.properties || {};
    Object.entries(props).forEach(([key, sub]) => {
      const subVal = generateExampleFromSchema(sub);
      result[key] = subVal === undefined ? defaultValueFromType(sub?.type) : subVal;
    });
    return result;
  }
  if (type === 'array') {
    const itemSchema = schema.items || {};
    const itemVal = generateExampleFromSchema(itemSchema);
    return itemVal === undefined ? [] : [itemVal];
  }
  return defaultValueFromType(type);
}

function queryRowsFromObject(query) {
  if (!query || typeof query !== 'object') return [{ name: '', type: 'string', required: false, description: '' }];
  const rows = Object.entries(query).map(([name, def]) => ({
    name,
    type: def?.type === 'number' ? 'number' : 'string',
    required: !!def?.required,
    description: def?.description || ''
  }));
  return rows.length > 0 ? rows : [{ name: '', type: 'string', required: false, description: '' }];
}

function queryObjectFromRows(rows) {
  const out = {};
  rows.forEach(({ name, type, required, description }) => {
    if (!isNonEmptyString(name)) return;
    const t = type === 'number' ? 'number' : 'string';
    out[name] = { type: t, required: !!required, description: description || '' };
  });
  return out;
}

function substitutePlaceholders(value, defaults) {
  if (value == null) return value;
  if (typeof value === 'string') {
    return value.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, p1) => {
      const def = defaults?.[p1];
      return def == null ? `{{${p1}}}` : String(def);
    });
  }
  if (Array.isArray(value)) return value.map(v => substitutePlaceholders(v, defaults));
  if (typeof value === 'object') {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      out[k] = substitutePlaceholders(v, defaults);
    });
    return out;
  }
  return value;
}

function buildCurl({ method, url, headers, body }) {
  const parts = ['curl'];
  if (method && method !== 'GET') parts.push(`-X ${method}`);
  if (headers && typeof headers === 'object') {
    Object.entries(headers).forEach(([k, v]) => {
      parts.push(`-H ${JSON.stringify(`${k}: ${v}`)}`);
    });
  }
  if (body != null && method && method !== 'GET' && method !== 'DELETE') {
    const jsonStr = JSON.stringify(body);
    parts.push(`-H "Content-Type: application/json"`);
    parts.push(`-d ${JSON.stringify(jsonStr)}`);
  }
  parts.push(JSON.stringify(url || ''));
  return parts.join(' ');
}

function normalizeBooleanFlag(value, defaultValue = false) {
  if (value === true || value === false) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['yes', 'true', '1'].includes(normalized)) return true;
    if (['no', 'false', '0'].includes(normalized)) return false;
  }
  return defaultValue;
}

const EmptyState = ({ onAdd }) => (
  <div className="mt-4 bg-white border border-gray-200 rounded-lg p-8 text-center">
    <div className="flex items-center justify-center mb-3">
      <Wrench className="w-10 h-10 text-gray-300" />
    </div>
    <p className="text-gray-700 mb-1" style={{ fontWeight: '550' }}>No Custom Tools yet</p>
    <p className="text-sm text-gray-500 mb-4">Add HTTP request, context injection, LLM call, or email tools.</p>
    <button onClick={onAdd} className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md">
      <Plus className="w-4 h-4" /> Add Tool
    </button>
  </div>
);

const ToolEditor = ({
  existingNames,
  initial,
  onCancel,
  onSave,
  availableLanguages,
  systemVariables,
  dynamicVariables,
  onAddDynamicVariable,
  llmModelOptions
}) => {
  const [toolName, setToolName] = useState(initial?.tool_name || '');
  const [toolType, setToolType] = useState(initial?.tool_type || 'http_request');
  const [description, setDescription] = useState(initial?.description || '');
  const [status, setStatus] = useState(initial?.status || 'active');
  const [method, setMethod] = useState(initial?.method || 'POST');
  const [url, setUrl] = useState(initial?.url || '');
  const [injectionContext, setInjectionContext] = useState(initial?.context_injection?.injection_context || '');
  const [llmModel, setLlmModel] = useState(initial?.llm_call?.model || 'gpt-4.1-mini');
  const [llmReasoningEffort, setLlmReasoningEffort] = useState(initial?.llm_call?.reasoning_effort || '');
  const [verificationEnabled, setVerificationEnabled] = useState(
    normalizeBooleanFlag(initial?.pre_tool_verification?.enabled, false)
  );
  const [verificationModel, setVerificationModel] = useState(initial?.pre_tool_verification?.model || 'gpt-6-luna');
  const [verificationReasoningEffort, setVerificationReasoningEffort] = useState(initial?.pre_tool_verification?.reasoning_effort || 'low');
  const [verificationInstructions, setVerificationInstructions] = useState(initial?.pre_tool_verification?.instructions ?? BOOKING_VERIFICATION_INSTRUCTIONS);
  const [llmPrompt, setLlmPrompt] = useState(initial?.llm_call?.prompt || '');
  const [recipientEmail, setRecipientEmail] = useState(initial?.email?.recipient_email || '');
  const [includeCallMetadata, setIncludeCallMetadata] = useState(
    normalizeBooleanFlag(initial?.email?.include_call_metadata, false)
  );
  const [responseSchemaText, setResponseSchemaText] = useState(() => {
    const schema = initial?.llm_call?.response_schema;
    if (!schema || typeof schema !== 'object' || Array.isArray(schema)) return '';
    try {
      return Object.keys(schema).length > 0 ? JSON.stringify(schema, null, 2) : '';
    } catch (_) {
      return '';
    }
  });
  const [headerRows, setHeaderRows] = useState(() => toRowsFromObject(initial?.headers));
  const [attachConversationIdHeader, setAttachConversationIdHeader] = useState(
    normalizeBooleanFlag(initial?.attach_conversation_id_header, false)
  );
  const [attachAgentIdHeader, setAttachAgentIdHeader] = useState(
    normalizeBooleanFlag(initial?.attach_agent_id_header, false)
  );
  const [queryRows, setQueryRows] = useState(() => queryRowsFromObject(initial?.query_parameters));
  const toYesNo = (val, fallback = 'No') => {
    if (val === 'Yes' || val === true) return 'Yes';
    if (val === 'No' || val === false) return 'No';
    return fallback;
  };
  const [availability, setAvailability] = useState(initial?.availability);
  const [startMessage, setStartMessage] = useState(initial?.start_message ?? '');
  const [startMessageRules, setStartMessageRules] = useState(initial?.start_message_rules);
  const displayedStartMessageRules = getMessageRules({ ...initial, start_message_rules: startMessageRules }, 'start_message');
  const startMessageConfiguration = {
    ...(startMessageRules === undefined ? {} : { start_message_rules: startMessageRules }),
    ...(initial?.start_message_language_wise === undefined ? {} : { start_message_language_wise: initial.start_message_language_wise }),
  };
  const [startDelay, setStartDelay] = useState(initial?.start_delay_seconds ?? 0);
  const [interruptStart, setInterruptStart] = useState(initial?.interrupt_start_message_on_result ?? false);
  const [startSound, setStartSound] = useState(initial?.start_sound ?? 'none');
  const [delayTillStartMessageSpoken, setDelayTillStartMessageSpoken] = useState(toYesNo(initial?.delay_till_start_message_spoken));
  const [cancelOnInterruption, setCancelOnInterruption] = useState(
    toYesNo(normalizeBooleanFlag(initial?.cancel_on_interruption, false), 'No')
  );
  const [timeoutMs, setTimeoutMs] = useState(() => {
    const v = initial?.timeout_ms;
    if (typeof v === 'number') return String(v);
    if (typeof v === 'string') return v;
    return '';
  });
  const [parametersSchemaText, setParametersSchemaText] = useState(() => {
    if (initial?.parameters && typeof initial.parameters === 'object' && !Array.isArray(initial.parameters)) {
      try {
        return Object.keys(initial.parameters).length > 0 ? JSON.stringify(initial.parameters, null, 2) : '';
      } catch (_) { return ''; }
    }
    return '';
  });
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);

  const isHttpRequest = toolType === 'http_request';
  const isContextInjection = toolType === 'context_injection';
  const isLlmCall = toolType === 'llm_call';
  const isSendEmail = toolType === 'send_email';
  const isGet = method === 'GET' || method === 'DELETE';
  const availableLlmModels = useMemo(() => {
    const configured = llmModelOptions && typeof llmModelOptions === 'object'
      ? llmModelOptions
      : {};
    return Object.keys(configured).length > 0 ? configured : DEFAULT_LLM_MODELS;
  }, [llmModelOptions]);
  const reasoningConfig = availableLlmModels?.[llmModel]?.reasoning_effort || {};
  const llmReasoningOptions = Array.isArray(reasoningConfig.options)
    ? reasoningConfig.options
    : [];
  const defaultLlmReasoningEffort = String(
    reasoningConfig.default_value || llmReasoningOptions[0] || ''
  ).trim().toLowerCase();
  const selectedLlmReasoningEffort = llmReasoningOptions.includes(
    String(llmReasoningEffort || '').trim().toLowerCase()
  )
    ? String(llmReasoningEffort).trim().toLowerCase()
    : defaultLlmReasoningEffort;
  const handleLlmModelChange = (model) => {
    const reasoningConfig = availableLlmModels?.[model]?.reasoning_effort || {};
    const options = Array.isArray(reasoningConfig.options) ? reasoningConfig.options : [];
    setLlmModel(model);
    setLlmReasoningEffort(String(reasoningConfig.default_value || options[0] || '').trim().toLowerCase());
  };
  const verificationReasoningConfig = availableLlmModels?.[verificationModel]?.reasoning_effort || {};
  const verificationReasoningOptions = Array.isArray(verificationReasoningConfig.options)
    ? verificationReasoningConfig.options
    : [];
  const selectedVerificationReasoningEffort = verificationReasoningOptions.includes(verificationReasoningEffort)
    ? verificationReasoningEffort
    : String(verificationReasoningConfig.default_value || verificationReasoningOptions[0] || '').trim().toLowerCase();
  const handleVerificationModelChange = (model) => {
    const config = availableLlmModels?.[model]?.reasoning_effort || {};
    const options = Array.isArray(config.options) ? config.options : [];
    setVerificationModel(model);
    setVerificationReasoningEffort(model === 'gpt-6-luna' && options.includes('low')
      ? 'low'
      : String(config.default_value || options[0] || '').trim().toLowerCase());
  };
  const toolInputVariables = useMemo(() => {
    const parsed = parseJsonSafely(parametersSchemaText);
    const properties = parsed.ok && parsed.value?.properties && typeof parsed.value.properties === 'object'
      ? parsed.value.properties
      : {};
    return Object.entries(properties).map(([name, definition]) => ({
      name,
      description: definition?.description || `Tool input: ${name}`,
      variable_type: definition?.type || 'string',
      category: 'Tool Input'
    }));
  }, [parametersSchemaText]);
  const promptDynamicVariables = useMemo(() => {
    const combined = [...(dynamicVariables || []), ...toolInputVariables];
    return combined.filter((variable, index) =>
      combined.findIndex(candidate => candidate?.name === variable?.name) === index
    );
  }, [dynamicVariables, toolInputVariables]);

  useEffect(() => {
    if (!isHttpRequest) {
      setWarnings([]);
      return;
    }
    const currentWarnings = [];
    const headersObj = toObjectFromRows(headerRows);
    const headersPlaceholders = extractPlaceholdersFromObject(headersObj);
    const urlPlaceholders = extractPlaceholdersFromString(url);
    const referenced = new Set([...headersPlaceholders, ...urlPlaceholders]);

    if (!isGet) {
      const schemaParsed = parseJsonSafely(parametersSchemaText);
      const paramNames = new Set(
        schemaParsed.ok && schemaParsed.value && schemaParsed.value.properties && typeof schemaParsed.value.properties === 'object'
          ? Object.keys(schemaParsed.value.properties)
          : []
      );

      referenced.forEach((p) => {
        if (!paramNames.has(p)) {
          currentWarnings.push(`Placeholder {{${p}}} is not defined in parameter schema.`);
        }
      });
    }

    setWarnings(currentWarnings);
  }, [headerRows, url, parametersSchemaText, isGet, isHttpRequest]);

  const headerKeySet = useMemo(() => new Set(headerRows.map(r => r.key).filter(k => isNonEmptyString(k))), [headerRows]);

  const handleAddHeader = () => setHeaderRows(prev => [...prev, { key: '', value: '' }]);
  const handleRemoveHeader = (idx) => setHeaderRows(prev => prev.filter((_, i) => i !== idx));
  const handleHeaderChange = (idx, field, value) => {
    setHeaderRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const handleAddQueryParam = () => setQueryRows(prev => [...prev, { name: '', type: 'string', required: false, description: '' }]);
  const handleRemoveQueryParam = (idx) => setQueryRows(prev => prev.filter((_, i) => i !== idx));
  const handleQueryChange = (idx, field, value) => {
    setQueryRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };



  const handleSave = () => {
    setError('');
    const feedbackError = validateToolFeedback({ start_delay_seconds: startDelay, start_sound: startSound, delay_till_start_message_spoken: delayTillStartMessageSpoken, interrupt_start_message_on_result: interruptStart });
    if (feedbackError) { setError(feedbackError); return; }

    const normalizedExisting = new Set((existingNames || []).map(n => (n || '').trim().toLowerCase()));
    const currentName = (toolName || '').trim();
    if (!isNonEmptyString(currentName)) {
      setError('Tool name is required');
      return;
    }
    if (RESERVED_TOOL_NAMES.has(currentName.toLowerCase())) {
      setError(`"${currentName}" is reserved for a standard tool. Choose a different name.`);
      return;
    }
    const isRename = currentName.toLowerCase() !== (initial?.tool_name || '').trim().toLowerCase();
    if (isRename && normalizedExisting.has(currentName.toLowerCase())) {
      setError('Tool name must be unique within the agent');
      return;
    }
    if (!isNonEmptyString(description)) {
      setError('Description is required');
      return;
    }

    let saved;
    if (isHttpRequest) {
      if (!HTTP_METHODS.includes(method)) {
        setError('HTTP method is required');
        return;
      }
      if (!isNonEmptyString(url)) {
        setError('URL is required');
        return;
      }

      if (verificationEnabled && !Object.prototype.hasOwnProperty.call(availableLlmModels, verificationModel)) {
        setError('Select an available verification model');
        return;
      }
      if (verificationEnabled && !isNonEmptyString(verificationInstructions)) {
        setError('Verification instructions are required');
        return;
      }
      const headersObj = toObjectFromRows(headerRows);
      if (headerRows.some((r) => !isNonEmptyString(r.key) && isNonEmptyString(r.value))) {
        setError('Header key cannot be empty when value is provided');
        return;
      }
      if (headerRows.filter((row) => isNonEmptyString(row.key)).length !== headerKeySet.size) {
        setError('Duplicate header keys are not allowed');
        return;
      }
      if (headerRows.some((row) => RESERVED_RUNTIME_HEADERS.has(String(row.key || '').trim().toLowerCase()))) {
        setError('Runtime identity headers are reserved. Use the attachment switches instead.');
        return;
      }

      const schemaParsed = parseJsonSafely(parametersSchemaText);
      if (!isGet && !schemaParsed.ok && isNonEmptyString(parametersSchemaText)) {
        setError('Parameters schema must be valid JSON');
        return;
      }
      const queryObj = queryObjectFromRows(queryRows);
      const timeoutVal = parseInt(timeoutMs, 10);

      saved = {
        tool_type: 'http_request',
        tool_name: currentName,
        status: status === 'inactive' ? 'inactive' : 'active',
        description: description || '',
        method,
        url,
        headers: headersObj,
        attach_conversation_id_header: attachConversationIdHeader,
        attach_agent_id_header: attachAgentIdHeader,
        ...(verificationEnabled || initial?.pre_tool_verification ? {
          pre_tool_verification: {
            enabled: verificationEnabled,
            model: verificationModel,
            ...(verificationReasoningOptions.length > 0
              ? { reasoning_effort: selectedVerificationReasoningEffort }
              : {}),
            instructions: verificationInstructions
          }
        } : {}),
        start_message: startMessage ?? '',
        ...startMessageConfiguration,
        start_delay_seconds: Number(startDelay),
        start_sound: startSound,
        interrupt_start_message_on_result: interruptStart,
        delay_till_start_message_spoken: delayTillStartMessageSpoken === 'Yes' ? 'Yes' : 'No',
        cancel_on_interruption: cancelOnInterruption === 'Yes',
        ...(schemaParsed.ok && schemaParsed.value && !isGet ? { parameters: schemaParsed.value } : {}),
        ...(Object.keys(queryObj).length > 0 ? { query_parameters: queryObj } : {}),
        ...(Number.isFinite(timeoutVal) && timeoutVal > 0 ? { timeout_ms: timeoutVal } : {})
      };
    } else if (isContextInjection) {
      if (!isNonEmptyString(injectionContext)) {
        setError('Injection context is required');
        return;
      }
      const schemaParsed = parseJsonSafely(parametersSchemaText);
      if (!schemaParsed.ok && isNonEmptyString(parametersSchemaText)) {
        setError('Parameters schema must be valid JSON');
        return;
      }
      if (schemaParsed.ok && schemaParsed.value != null && (typeof schemaParsed.value !== 'object' || Array.isArray(schemaParsed.value))) {
        setError('Parameters schema must be a JSON object');
        return;
      }
      const defaultContextSchema = {
        type: 'object',
        properties: {},
        required: []
      };
      saved = {
        tool_type: 'context_injection',
        tool_name: currentName,
        status: status === 'inactive' ? 'inactive' : 'active',
        description: description || '',
        start_message: startMessage ?? '',
        ...startMessageConfiguration,
        start_delay_seconds: Number(startDelay),
        start_sound: startSound,
        interrupt_start_message_on_result: interruptStart,
        delay_till_start_message_spoken: delayTillStartMessageSpoken === 'Yes' ? 'Yes' : 'No',
        cancel_on_interruption: cancelOnInterruption === 'Yes',
        parameters: schemaParsed.ok && schemaParsed.value ? schemaParsed.value : defaultContextSchema,
        context_injection: {
          injection_context: injectionContext
        }
      };
    } else if (isSendEmail) {
      if (!/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(currentName)) {
        setError('Email tool name must use 1-64 letters, numbers, or underscores and cannot start with a number');
        return;
      }
      if (!isValidEmailAddress(recipientEmail)) {
        setError('Enter a valid recipient email address');
        return;
      }
      saved = {
        tool_type: 'send_email',
        tool_name: currentName,
        status: status === 'inactive' ? 'inactive' : 'active',
        description: description || '',
        start_message: startMessage ?? '',
        ...startMessageConfiguration,
        start_delay_seconds: Number(startDelay),
        start_sound: startSound,
        interrupt_start_message_on_result: interruptStart,
        delay_till_start_message_spoken: delayTillStartMessageSpoken === 'Yes' ? 'Yes' : 'No',
        cancel_on_interruption: cancelOnInterruption === 'Yes',
        parameters: EMAIL_TOOL_PARAMETERS,
        email: {
          recipient_email: recipientEmail.trim(),
          include_call_metadata: includeCallMetadata
        }
      };
    } else {
      if (!/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(currentName)) {
        setError('LLM tool name must use 1-64 letters, numbers, or underscores and cannot start with a number');
        return;
      }
      if (!Object.prototype.hasOwnProperty.call(availableLlmModels, llmModel)) {
        setError('LLM model is required');
        return;
      }
      if (!isNonEmptyString(llmPrompt)) {
        setError('Prompt is required');
        return;
      }
      const parametersParsed = parseJsonSafely(parametersSchemaText);
      if (!parametersParsed.ok && isNonEmptyString(parametersSchemaText)) {
        setError('Parameter schema must be valid JSON');
        return;
      }
      if (parametersParsed.value != null && (typeof parametersParsed.value !== 'object' || Array.isArray(parametersParsed.value))) {
        setError('Parameter schema must be a JSON object');
        return;
      }
      const responseSchemaParsed = parseJsonSafely(responseSchemaText);
      if (!responseSchemaParsed.ok && isNonEmptyString(responseSchemaText)) {
        setError('Output schema must be valid JSON');
        return;
      }
      if (responseSchemaParsed.value != null && (typeof responseSchemaParsed.value !== 'object' || Array.isArray(responseSchemaParsed.value))) {
        setError('Output schema must be a JSON object');
        return;
      }
      const defaultInputSchema = {
        type: 'object',
        properties: {},
        required: []
      };
      const timeoutVal = parseInt(timeoutMs, 10);
      if (Number.isFinite(timeoutVal) && (timeoutVal < 1000 || timeoutVal > 30000)) {
        setError('Timeout must be between 1000 and 30000 ms');
        return;
      }
      const inputSchema = parametersParsed.value && Object.keys(parametersParsed.value).length > 0
        ? parametersParsed.value
        : defaultInputSchema;
      saved = {
        tool_type: 'llm_call',
        tool_name: currentName,
        status: status === 'inactive' ? 'inactive' : 'active',
        description: description || '',
        start_message: startMessage ?? '',
        ...startMessageConfiguration,
        start_delay_seconds: Number(startDelay),
        start_sound: startSound,
        interrupt_start_message_on_result: interruptStart,
        delay_till_start_message_spoken: delayTillStartMessageSpoken === 'Yes' ? 'Yes' : 'No',
        cancel_on_interruption: cancelOnInterruption === 'Yes',
        parameters: inputSchema,
        llm_call: {
          model: llmModel,
          ...(llmReasoningOptions.length > 0
            ? { reasoning_effort: selectedLlmReasoningEffort }
            : {}),
          prompt: llmPrompt,
          ...(responseSchemaParsed.value && Object.keys(responseSchemaParsed.value).length > 0
            ? { response_schema: responseSchemaParsed.value }
            : {})
        },
        timeout_ms: Number.isFinite(timeoutVal) && timeoutVal > 0 ? timeoutVal : 20000
      };
    }
    const messageError = validateMessageRules(displayedStartMessageRules);
    if (messageError) { setError(messageError); return; }
    onSave({ ...saved, ...(availability ? { availability } : {}) });
  };

  const preview = useMemo(() => {
    const headersObj = toObjectFromRows(headerRows);
    const resolvedHeaders = {
      ...headersObj,
      ...(attachConversationIdHeader
        ? { [CONVERSATION_ID_HEADER]: 'RUNTIME_CONVERSATION_ID' }
        : {}),
      ...(attachAgentIdHeader
        ? { [AGENT_ID_HEADER]: 'RUNTIME_AGENT_ID' }
        : {})
    };
    // Build query string from queryRows with sample values
    const qp = queryObjectFromRows(queryRows);
    const queryParts = Object.entries(qp).map(([k, def]) => {
      const sample = def.type === 'number' ? 123 : 'TEST_STRING';
      return `${encodeURIComponent(k)}=${encodeURIComponent(String(sample))}`;
    });
    const urlWithQuery = queryParts.length > 0 ? `${url}${url.includes('?') ? '&' : '?'}${queryParts.join('&')}` : url;

    // Build example body from schema for non-GET/DELETE
    let body = undefined;
    if (!(method === 'GET' || method === 'DELETE')) {
      const schemaParsed = parseJsonSafely(parametersSchemaText);
      if (schemaParsed.ok && schemaParsed.value && Object.keys(schemaParsed.value || {}).length > 0) {
        body = generateExampleFromSchema(schemaParsed.value);
      }
    }

    const curl = buildCurl({ method, url: urlWithQuery, headers: resolvedHeaders, body });
    const unresolved = new Set([
      ...extractPlaceholdersFromObject(headersObj),
      ...extractPlaceholdersFromString(urlWithQuery)
    ]);
    return { resolvedHeaders, curl, unresolved: Array.from(unresolved), urlWithQuery, body };
  }, [headerRows, attachConversationIdHeader, attachAgentIdHeader, method, url, queryRows, parametersSchemaText]);

  const copyCurl = async () => {
    try {
      await navigator.clipboard.writeText(preview.curl);
    } catch (_) {}
  };

  return (
    <div className="mt-4 bg-white border border-gray-200 rounded-lg">
      <div className="p-4 grid grid-cols-1 gap-4 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tool Name<span className="text-red-500">*</span></label>
            <input value={toolName} onChange={(e) => setToolName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500" placeholder="Unique name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <ModernTimeDropdown value={status} onChange={(e) => setStatus(e.target.value)} options={["active", "inactive"]} width="w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tool Type</label>
            <ModernTimeDropdown value={toolType} onChange={(e) => setToolType(e.target.value)} options={TOOL_TYPES} width="w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description<span className="text-red-500">*</span></label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500 resize-vertical" placeholder="Describe the tool purpose and how it will be used" />
          </div>
        </div>

        <ToolAvailability availableLanguages={availableLanguages} value={availability} onChange={setAvailability} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <MessageWithOverrides
              label="Start Message"
              value={startMessage}
              onChange={(e) => setStartMessage(e.target.value)}
              placeholder="Optional: message to speak before triggering this tool"
              rows={2}
              systemVariables={systemVariables}
              dynamicVariables={dynamicVariables}
              onAddDynamicVariable={onAddDynamicVariable}
              rules={displayedStartMessageRules}
              availableLanguages={availableLanguages}
              onRulesChange={setStartMessageRules}
            />

          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Message Delay (seconds)</label>
            <input type="number" min={0} max={44} step="any" value={startDelay} onChange={e => setStartDelay(e.target.value === '' ? 0 : Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            <p className="text-xs text-gray-500 mt-1">Voice tools execute immediately. Speak the start message only if still running after this delay. 0 speaks immediately.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sound during Start Message Delay</label>
            <ModernTimeDropdown ariaLabel="Sound during Start Message Delay" value={startSound} onChange={e => setStartSound(e.target.value)} options={[['none', 'None'], ['typing', 'Typing only'], ['typing_background', 'Typing with background noise']]} width="w-full" />
            <p className="text-xs text-gray-500 mt-1">Play during the voice Start delay. Stops before speech or when the tool finishes.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Interrupt start message when tool finishes</label>
            <ModernTimeDropdown ariaLabel="Interrupt start message when tool finishes" value={String(interruptStart)} onChange={e => setInterruptStart(e.target.value === 'true')} options={[['false', 'No'], ['true', 'Yes']]} width="w-full" />
            <p className="text-xs text-gray-500 mt-1">Stop the tool's start announcement when its result arrives. Default: No.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Delay Execution till start message has spoken</label>
            <ModernTimeDropdown
              value={delayTillStartMessageSpoken}
              onChange={(e) => setDelayTillStartMessageSpoken(e.target.value)}
              options={['No', 'Yes']}
              width="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cancel on interruption</label>
            <ModernTimeDropdown
              value={cancelOnInterruption}
              onChange={(e) => setCancelOnInterruption(e.target.value)}
              options={['Yes', 'No']}
              width="w-full"
            />
          </div>
        </div>

        {isHttpRequest && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Method<span className="text-red-500">*</span></label>
                <ModernTimeDropdown value={method} onChange={(e) => setMethod(e.target.value)} options={HTTP_METHODS} width="w-full" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">URL<span className="text-red-500">*</span></label>
                <input value={url} onChange={(e) => setUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500" placeholder="https://api.example.com/path" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Timeout (ms)</label>
                <input type="number" min="0" step="1" value={timeoutMs} onChange={(e) => setTimeoutMs(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500" placeholder="e.g., 20000" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Runtime identity headers</label>
              <div className="space-y-2 rounded-md border border-gray-200 p-3">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={attachConversationIdHeader}
                    onChange={(e) => setAttachConversationIdHeader(e.target.checked)}
                  />
                  <span>
                    Attach conversation ID header
                    <code className="ml-1 text-xs text-gray-600">({CONVERSATION_ID_HEADER})</code>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={attachAgentIdHeader}
                    onChange={(e) => setAttachAgentIdHeader(e.target.checked)}
                  />
                  <span>
                    Attach agent/bot ID header
                    <code className="ml-1 text-xs text-gray-600">({AGENT_ID_HEADER})</code>
                  </span>
                </label>
                <p className="text-xs text-amber-700">
                  These runtime identifiers are sensitive. Enable them only for trusted HTTPS endpoints.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Headers</label>
              <div className="space-y-2">
                {headerRows.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2">
                    <input className="col-span-5 px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500" placeholder="Header Key" value={row.key} onChange={(e) => handleHeaderChange(idx, 'key', e.target.value)} />
                    <input className="col-span-6 px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500" placeholder="Header Value" value={row.value} onChange={(e) => handleHeaderChange(idx, 'value', e.target.value)} />
                    <button className="col-span-1 px-2 py-2 text-gray-600 hover:text-red-600" onClick={() => handleRemoveHeader(idx)}>
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button onClick={handleAddHeader} className="inline-flex items-center gap-2 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">
                  <Plus className="w-4 h-4" /> Add Header
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Query Parameters</label>
              <div className="space-y-2">
                {queryRows.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <input className="col-span-3 px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500" placeholder="name" value={row.name} onChange={(e) => handleQueryChange(idx, 'name', e.target.value)} />
                    <ModernTimeDropdown className="col-span-2" value={row.type} onChange={(e) => handleQueryChange(idx, 'type', e.target.value)} options={["string", "number"]} width="w-full" />
                    <input className="col-span-5 px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500" placeholder="description" value={row.description} onChange={(e) => handleQueryChange(idx, 'description', e.target.value)} />
                    <label className="col-span-1 inline-flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={!!row.required} onChange={(e) => handleQueryChange(idx, 'required', e.target.checked)} /> Required
                    </label>
                    <button className="col-span-1 px-2 py-2 text-gray-600 hover:text-red-600" onClick={() => handleRemoveQueryParam(idx)}>
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button onClick={handleAddQueryParam} className="inline-flex items-center gap-2 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">
                  <Plus className="w-4 h-4" /> Add Query Param
                </button>
              </div>
            </div>

            {!isGet && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Parameter Schema (OpenAI compatible JSON Schema)</label>
                <textarea
                  value={parametersSchemaText}
                  onChange={(e) => setParametersSchemaText(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500 resize-vertical"
                  placeholder={`{\n  "type": "object",\n  "properties": {\n    "user_id": { "type": "string", "description": "User identifier" },\n    "limit": { "type": "integer", "description": "Max items to fetch" }\n  },\n  "required": ["user_id"]\n}`}
                />
                <div className="text-xs text-gray-500 mt-1">Define parameters for placeholders like {'{{user_id}}'} in headers/URL.</div>
              </div>
            )}
          </>
        )}

        {isHttpRequest && (
          <div className="space-y-4 rounded-md border border-gray-200 bg-gray-50 p-3">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={verificationEnabled}
                onChange={(e) => setVerificationEnabled(e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-700">Run Pre Tool Verification</span>
                <span className="block text-xs text-gray-500 mt-1">
                  Check proposed arguments against the conversation before this HTTP request runs. Off by default.
                </span>
              </span>
            </label>
            {verificationEnabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Verification Model</label>
                    <ModernTimeDropdown ariaLabel="Verification Model" value={verificationModel} onChange={(e) => handleVerificationModelChange(e.target.value)} options={[...(!Object.prototype.hasOwnProperty.call(availableLlmModels, verificationModel) ? [[verificationModel, `${verificationModel} (unavailable)`]] : []), ...Object.entries(availableLlmModels).map(([modelId, config]) => [modelId, config?.label || modelId])]} width="w-full" />
                  </div>
                  {verificationReasoningOptions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reasoning Effort</label>
                      <ModernTimeDropdown ariaLabel="Verification Reasoning Effort" value={selectedVerificationReasoningEffort} onChange={(e) => setVerificationReasoningEffort(e.target.value)} options={verificationReasoningOptions.map(option => [option, option.charAt(0).toUpperCase() + option.slice(1)])} width="w-full" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Verification Instructions<span className="text-red-500">*</span></label>
                  <p className="text-xs text-gray-500 mb-2">
                    The prefilled example is for booking-confirmation tools. Adapt it to this HTTP tool's purpose.
                  </p>
                  <textarea
                    value={verificationInstructions}
                    onChange={(e) => setVerificationInstructions(e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500 resize-vertical"
                  />
                </div>
                <div className="rounded-md border border-indigo-100 bg-indigo-50 p-3 text-xs text-gray-700">
                  <div className="font-medium text-gray-800 mb-2">Fixed structured output</div>
                  <p>The output schema is fixed and sent to AI Gateway.</p>
                  <pre className="mt-2 whitespace-pre-wrap">{'Allow: {"allow":"yes"}\nBlock: {"allow":"no","reason":"Confirmed 4:30 PM (16:30); proposed 14:30."}'}</pre>
                  <p className="mt-2">Blocked requests return the verifier reason in the tool result.</p>
                  <p className="mt-1">An identical retry after a verifier rejection is allowed.</p>
                  <p className="mt-1">If verification is unavailable, the HTTP request runs and records that verification was unavailable.</p>
                </div>
              </>
            )}
          </div>
        )}

        {(isContextInjection || isLlmCall) && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Input Parameter Schema (OpenAI compatible JSON Schema)</label>
              <textarea
                value={parametersSchemaText}
                onChange={(e) => setParametersSchemaText(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500 resize-vertical"
                placeholder={`{\n  "type": "object",\n  "properties": {},\n  "required": []\n}`}
              />
              <div className="text-xs text-gray-500 mt-1">If left empty, a default empty object schema will be used.</div>
            </div>
            {isContextInjection && <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Injection Context<span className="text-red-500">*</span></label>
              <textarea
                value={injectionContext}
                onChange={(e) => setInjectionContext(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500 resize-vertical"
                placeholder="Context to append to LLM prompt when this tool is fired."
              />
              <div className="text-xs text-gray-500 mt-1">This text is appended as additional context for the model when the tool runs.</div>
            </div>}
            {isLlmCall && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model<span className="text-red-500">*</span></label>
                  <ModernTimeDropdown ariaLabel="Model" value={llmModel} onChange={(e) => handleLlmModelChange(e.target.value)} options={Object.entries(availableLlmModels).map(([modelId, config]) => [modelId, config?.label || modelId])} width="w-full" />
                </div>
                {llmReasoningOptions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reasoning Effort</label>
                    <ModernTimeDropdown ariaLabel="Reasoning Effort" value={selectedLlmReasoningEffort} onChange={(e) => setLlmReasoningEffort(e.target.value)} options={llmReasoningOptions.map(option => [option, option.charAt(0).toUpperCase() + option.slice(1)])} width="w-full" />
                  </div>
                )}
                <TextareaWithVariables
                  label="Prompt"
                  value={llmPrompt}
                  onChange={(e) => setLlmPrompt(e.target.value)}
                  placeholder="Enter the instruction and insert system, dynamic, or tool-input variables."
                  rows={7}
                  maxLength={20000}
                  required
                  systemVariables={systemVariables}
                  dynamicVariables={promptDynamicVariables}
                  onAddDynamicVariable={onAddDynamicVariable}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Output Schema (optional JSON Schema)</label>
                  <textarea
                    value={responseSchemaText}
                    onChange={(e) => setResponseSchemaText(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500 resize-vertical"
                    placeholder={`{\n  "type": "object",\n  "properties": {\n    "result": { "type": "string" }\n  },\n  "required": ["result"]\n}`}
                  />
                  <div className="text-xs text-gray-500 mt-1">When omitted, the LLM response is returned as plain text.</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timeout (ms)</label>
                  <input
                    type="number"
                    min="1000"
                    max="30000"
                    step="1000"
                    value={timeoutMs}
                    onChange={(e) => setTimeoutMs(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500"
                    placeholder="20000"
                  />
                </div>
              </>
            )}
          </>
        )}

        {isSendEmail && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Email<span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500"
                placeholder="notifications@example.com"
                autoComplete="off"
              />
              <div className="text-xs text-gray-500 mt-1">
                This address is configured here and is not exposed as an input to the voice agent.
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
              <input
                type="checkbox"
                checked={includeCallMetadata}
                onChange={(e) => setIncludeCallMetadata(e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-700">Include call metadata</span>
                <span className="block text-xs text-gray-500 mt-1">
                  Includes Customer Number, Conversation ID, Agent ID, and Agent Name together on one line.
                </span>
              </span>
            </label>

            <div className="rounded-md border border-indigo-100 bg-indigo-50 p-3 text-xs text-gray-700">
              <div className="font-medium text-gray-800 mb-2">Email tool inputs</div>
              <div><span className="font-medium">subject</span> — required, generated by the voice agent</div>
              <div><span className="font-medium">message</span> — required, generated by the voice agent</div>
              {includeCallMetadata && (
                <pre
                  className="mt-3 whitespace-pre-wrap rounded border border-indigo-100 bg-white p-2 italic"
                  style={{ color: '#6B7280', fontSize: '12px' }}
                >
                  {'Customer Number - <value>, Conversation ID - <value>, Agent ID - <value>, Agent Name - <value>'}
                </pre>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
        )}

        {warnings.length > 0 && (
          <div className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                <div className="font-medium">Warnings (non-blocking):</div>
                <ul className="list-disc ml-5 mt-1">
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}

        {isHttpRequest && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm font-medium text-gray-700">Preview (no network)</div>
              <button onClick={copyCurl} className="inline-flex items-center gap-2 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">
                <Copy className="w-4 h-4" /> Copy as cURL
              </button>
            </div>
            <div className="text-xs text-gray-700">
              <div className="mb-1"><span className="font-medium">Method:</span> {method}</div>
              <div className="mb-1"><span className="font-medium">URL:</span> {url}</div>
              <div className="mb-1"><span className="font-medium">Headers:</span> <pre className="inline whitespace-pre-wrap">{JSON.stringify(preview.resolvedHeaders || {}, null, 2)}</pre></div>
              null
              {preview.unresolved.length > 0 && (
                <div className="mt-2 text-yellow-800">Unresolved placeholders: {preview.unresolved.map(p => `{{${p}}}`).join(', ')}</div>
              )}
              <div className="mt-2"><span className="font-medium">cURL:</span>
                <pre className="mt-1 bg-white border border-gray-200 rounded p-2 overflow-auto">{preview.curl}</pre>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white">Save Tool</button>
        </div>
      </div>
    </div>
  );
};

const CustomToolsConfig = ({ tools = [], onChange, availableLanguages = [], systemVariables, dynamicVariables, onAddDynamicVariable, llmModelOptions }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingInitial, setEditingInitial] = useState(null);

  const existingNames = useMemo(() => (tools || []).map(t => t?.tool_name).filter(Boolean), [tools]);

  const startAdd = () => {
    setEditingIndex(null);
    setEditingInitial({
      tool_type: 'http_request',
      tool_name: '',
      status: 'active',
      description: '',
      method: 'POST',
      url: '',
      headers: {},
      parameters: {},
      context_injection: { injection_context: '' },
      llm_call: {
        model: 'gpt-4.1-mini',
        prompt: ''
      },
      email: {
        recipient_email: '',
        include_call_metadata: false
      },
      defaults: {},
      start_message: '',
      start_message_rules: [],
      delay_till_start_message_spoken: 'No',
      cancel_on_interruption: false,
      timeout_ms: 20000
    });
    setEditorOpen(true);
  };

  const startEdit = (index) => {
    const t = tools[index];
    setEditingIndex(index);
    setEditingInitial(t);
    setEditorOpen(true);
  };

  const handleDelete = (index) => {
    const t = tools[index];
    const ok = window.confirm(`Delete tool \"${t?.tool_name}\"?`);
    if (!ok) return;
    const next = tools.filter((_, i) => i !== index);
    onChange(next);
  };

  const handleSaveTool = (saved) => {
    let next = Array.isArray(tools) ? [...tools] : [];
    if (editingIndex == null) {
      next.push(saved);
    } else {
      next[editingIndex] = saved;
    }
    onChange(next);
    setEditorOpen(false);
    setEditingIndex(null);
    setEditingInitial(null);
  };

  return (
    <div className="border-t pt-6">
      <div className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )}
            <Wrench className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">Custom Tools</h3>
            <p className="text-sm text-gray-600">Configure HTTP, context injection, LLM call, and email tools for your agent</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">{(tools || []).length} tools</div>
          {isExpanded && (
            <button onClick={(e) => { e.stopPropagation(); startAdd(); }} className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md">
              <Plus className="w-4 h-4" /> Add Tool
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4">
          {(tools || []).length === 0 && !editorOpen && (
            <EmptyState onAdd={startAdd} />
          )}

          {(tools || []).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method / Model</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL / Recipient</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(tools || []).map((t, idx) => (
                      <tr key={`${t?.tool_name || 'tool'}-${idx}`} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{t?.tool_name || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-gray-50 text-gray-800">{t?.tool_type || 'http_request'}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${t?.status === 'inactive' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'}`}>{t?.status || 'active'}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{t?.tool_type === 'send_email' ? '' : (t?.method || t?.llm_call?.model || '-')}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm max-w-xs truncate" title={t?.url || t?.email?.recipient_email}>{t?.url || t?.email?.recipient_email || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button className="px-2 py-1 text-gray-600 hover:text-indigo-700" onClick={() => startEdit(idx)}>
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button className="px-2 py-1 text-gray-600 hover:text-red-700" onClick={() => handleDelete(idx)}>
                              <Trash className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {editorOpen && (
            <ToolEditor
              existingNames={existingNames}
              initial={editingInitial}
              onCancel={() => { setEditorOpen(false); setEditingIndex(null); setEditingInitial(null); }}
              onSave={handleSaveTool}
              availableLanguages={availableLanguages}
              systemVariables={systemVariables}
              dynamicVariables={dynamicVariables}
              onAddDynamicVariable={onAddDynamicVariable}
              llmModelOptions={llmModelOptions}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CustomToolsConfig;
